import type { BrandIntelligence } from '@/features/reports/api';
import { BRANDS_COPY as COPY } from '@/features/reports/copy';
import { BRAND_REPORT_STATE } from '@/features/reports/tabs';
import type { PdfDocument } from '@/features/orders/shipping/PdfViewer';

/*
  Generate report (ALS-93): the brand report PDF as the document viewer opens it, and when there is
  one to give. Kept out of the components so they can be tested on their own.
*/

/** Where Generate report is: at rest, setting the PDF, done for a moment, or failed and back at rest. */
export const GENERATE_STATE = {
    Idle: 'Idle',
    Generating: 'Generating',
    Ready: 'Ready',
} as const;

export type GenerateState = (typeof GENERATE_STATE)[keyof typeof GENERATE_STATE];

/** Only a report with figures has a PDF: every empty state has nothing to give. */
export function canGenerate(report: BrandIntelligence | undefined): boolean {
    return report?.state === BRAND_REPORT_STATE.Report;
}

/**
 * "a-list-brand-report-bottega-veneta-q3-2026": the API's own name for the file
 * (BrandReportFileName), so Download saves it under the name the server gives it. A name with
 * nothing a slug can hold, one written in another script, is named by its id, "brand-41".
 */
export function brandReportFileName(brandId: number, brand: string, quarter: string): string {
    return `a-list-brand-report-${slug(brand) || `brand-${brandId}`}-${slug(quarter)}.pdf`;
}

function slug(text: string): string {
    return text
        .replaceAll('&', ' and ')
        .normalize('NFD')
        .replace(/\p{Mn}/gu, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

/** The report in the viewer: its PDF, already fetched, at `src`. */
export function brandReportDocument(report: BrandIntelligence, src: string): PdfDocument {
    return {
        title: COPY.generate.title,
        subtitle: COPY.generate.subtitle(report.brand, report.quarter.label),
        url: src,
        fileName: brandReportFileName(report.brandId, report.brand, report.quarter.label),
    };
}
